import type { HealthSample, HierarchyTree, NodeStatus, OsFamily } from '@occ/contracts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { appendSample, type HealthSeries } from '../data/health';
import { fetchHierarchyTree } from '../data/nodes';
import { getLiveClient, type ConnectionState } from '../live/socket';
import { setHierarchyIndex, type NodeLocation } from './index-store';

/**
 * Estado de la jerarquía, vivo.
 *
 * El árbol se carga una vez y a partir de ahí lo mantiene al día el WebSocket:
 * volver a pedir `/hierarchy/tree` en cada evento convertiría cada latido de
 * cada nodo en una consulta a la base. Solo se recarga cuando aparece un nodo
 * que no estaba —un enrollment recién hecho— o cuando alguien lo pide.
 */

export type FlatNode = {
  id: string;
  alias: string;
  status: NodeStatus;
  osFamily: OsFamily | null;
  clientId: string;
  clientName: string;
  branchId: string;
  branchName: string;
};

type HierarchyContextValue = {
  tree: HierarchyTree | null;
  nodes: FlatNode[];
  /** Última muestra de salud recibida por el canal en vivo, por nodo. */
  healthByNode: Record<string, HealthSample>;
  /**
   * Serie que el panel va acumulando de ese mismo canal.
   *
   * Es el respaldo del histórico mientras el backend no persista las muestras:
   * se pierde al recargar y solo cubre lo que la pestaña lleve abierta, pero
   * dibuja una tendencia real en vez de un único punto.
   */
  healthSeriesByNode: Record<string, HealthSeries>;
  loading: boolean;
  error: string | null;
  connection: ConnectionState;
  refresh: () => void;
};

const HierarchyContext = createContext<HierarchyContextValue | null>(null);

const flatten = (tree: HierarchyTree | null): FlatNode[] => {
  if (!tree) return [];
  return tree.clients.flatMap((client) =>
    client.branches.flatMap((branch) =>
      branch.nodes.map((node) => ({
        id: node.id,
        alias: node.alias,
        status: node.status,
        osFamily: node.osFamily,
        clientId: client.id,
        clientName: client.name,
        branchId: branch.id,
        branchName: branch.name,
      })),
    ),
  );
};

/** Reemplaza el estado de un nodo sin tocar el resto del árbol. */
const withNodeStatus = (
  tree: HierarchyTree,
  nodeId: string,
  status: NodeStatus,
): HierarchyTree => ({
  clients: tree.clients.map((client) => ({
    ...client,
    branches: client.branches.map((branch) => {
      if (!branch.nodes.some((node) => node.id === nodeId)) return branch;
      const nodes = branch.nodes.map((node) => (node.id === nodeId ? { ...node, status } : node));
      return {
        ...branch,
        nodes,
        onlineNodeCount: nodes.filter((node) => node.status === 'online').length,
      };
    }),
  })),
});

export function HierarchyProvider({ children }: { children: ReactNode }) {
  const [tree, setTree] = useState<HierarchyTree | null>(null);
  const [healthByNode, setHealthByNode] = useState<Record<string, HealthSample>>({});
  const [healthSeriesByNode, setHealthSeriesByNode] = useState<Record<string, HealthSeries>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('connecting');

  // Se lee dentro del manejador de eventos; una dependencia más provocaría
  // resuscribirse a cada cambio de estado de un nodo.
  const knownNodeIds = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchHierarchyTree();
      setTree(next);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No se pudo cargar la jerarquía');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // El índice nodo→(cliente,sucursal) alimenta al control de acceso, que vive
  // fuera de React y no puede leer este contexto.
  useEffect(() => {
    if (!tree) return;
    const locations = new Map<string, NodeLocation>();
    const ids = new Set<string>();
    let tenantId = '';
    for (const client of tree.clients) {
      tenantId = client.tenantId;
      for (const branch of client.branches) {
        for (const node of branch.nodes) {
          locations.set(node.id, { clientId: client.id, branchId: branch.id });
          ids.add(node.id);
        }
      }
    }
    knownNodeIds.current = ids;
    if (tenantId) setHierarchyIndex(tenantId, locations);
  }, [tree]);

  useEffect(() => {
    const client = getLiveClient();
    setConnection(client.getState());

    const unsubscribeState = client.onStateChange(setConnection);
    const unsubscribe = client.subscribe((event) => {
      if (event.type === 'node.health') {
        setHealthByNode((current) => ({ ...current, [event.nodeId]: event.health }));
        setHealthSeriesByNode((current) => ({
          ...current,
          [event.nodeId]: appendSample(current[event.nodeId], event.health),
        }));
        return;
      }
      if (event.type !== 'node.status') return;

      if (!knownNodeIds.current.has(event.nodeId)) {
        // Nodo desconocido: normalmente un enrollment recién completado.
        void load();
        return;
      }
      setTree((current) =>
        current ? withNodeStatus(current, event.nodeId, event.status) : current,
      );
    });

    return () => {
      unsubscribe();
      unsubscribeState();
    };
  }, [load]);

  const value = useMemo<HierarchyContextValue>(
    () => ({
      tree,
      nodes: flatten(tree),
      healthByNode,
      healthSeriesByNode,
      loading,
      error,
      connection,
      refresh: () => void load(),
    }),
    [tree, healthByNode, healthSeriesByNode, loading, error, connection, load],
  );

  return <HierarchyContext.Provider value={value}>{children}</HierarchyContext.Provider>;
}

export function useHierarchy(): HierarchyContextValue {
  const context = useContext(HierarchyContext);
  if (!context) throw new Error('useHierarchy fuera de HierarchyProvider');
  return context;
}
